/**
 * Button appearance.
 *
 * Separate from the component so that file exports only a component (fast
 * refresh), and so a variant can be reused by a link that must look like a
 * button.
 *
 * Every value resolves to a design token — PRD §9.1 permits no colour that is
 * not in the design system.
 *
 * **Shape and type come from the reference prototype's `.btn`** (§4.5): a full
 * pill in the display font at weight 700, with a 1.5px transparent border so
 * that the outlined `secondary` variant is the same size as the filled ones and
 * text does not shift by 1.5px between them.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 5.6, 6.5.2
 */
import { cva, type VariantProps } from 'class-variance-authority';

export const buttonVariants = cva(
  /* `min-h-11` on every variant: 44px is the smallest reliable touch target,
     and PRD §9.7 applies it to pointer sizes too, not only to phones. */
  [
    'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full',
    'border-[1.5px] border-transparent',
    'font-display text-[13px] font-bold whitespace-nowrap',
    'transition-colors disabled:pointer-events-none disabled:opacity-60',
  ].join(' '),
  {
    variants: {
      variant: {
        /* Navy is the prototype's primary. Blue remains the *interactive*
           colour for links and focus (PRD §9.2); a filled button is a surface,
           and the DS gives navy 25% of the interface against blue's 12%. */
        primary: 'bg-navy text-white hover:bg-navy-mid',
        /* The one place chrome may be green. The DS reserves green for data
           (§4.2) but allots it 8% of the interface, and the prototype spends
           that share here — on the single create action per page. Navy on
           green-brand, never white: measured, white on green-brand is 1.90:1
           and fails AA outright, while navy-bg reaches 9.08:1. The hover swaps to
           white on green-dark (6.91:1) rather than darkening navy on green,
           which would drop below 4.5. */
        action: 'bg-green-brand text-navy hover:bg-green-dark hover:text-white',
        secondary: 'border-gray-200 text-navy bg-transparent hover:bg-gray-100',
        ghost: 'text-body hover:bg-gray-100',
        /* Reads as a link but keeps a button's semantics and hit area. */
        link: 'text-blue px-1 hover:underline',
        /* Destructive actions read as destructive before they are clicked. */
        danger: 'bg-sem-bad text-white hover:opacity-90',
      },
      size: {
        sm: 'px-3.5 text-xs',
        md: 'px-[22px]',
        lg: 'w-full px-7 text-sm',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;
